import { useState } from "react";
import { useAcceptTerms } from "@/features/terms/hooks/useAcceptTerms";
import { BotMessage } from "../../components/BotMessage";
import { TurnMessages } from "../../components/TurnMessages";
import { QuickReplies } from "../../components/QuickReplies";
import { TypingIndicator } from "../../components/TypingIndicator";
import { ChatTurnLayout } from "../../components/ChatTurnLayout";
import { useTurnMessages } from "../../hooks/useTurnMessages";
import { chatCopy } from "../../engine/copy";
import type { ChatTurnProps } from "../../types/chat";
import { preliminaryConsentLines } from "../consentLines";

const ConsentReply = { Accept: "accept", Why: "why" } as const;

// BC and ANAF consent in one answer, before the BC score. The offer is
// requested after KYC: right away when KYC is already done, or an earlier
// request still waiting on the consent would send the user to give it again.
export function BcConsentTurn({
  facts,
  isPromptTyped,
  onAnswered,
}: ChatTurnProps) {
  const { messages, addUser, addBot, addError } = useTurnMessages();
  // Once explained, only the consent itself is left to give.
  const [isWhyAnswered, setIsWhyAnswered] = useState(false);
  // The bot types while the consent is saved, until the next step takes
  // over; the replies come back only when saving failed.
  const [isSaving, setIsSaving] = useState(false);
  const { acceptTerms } = useAcceptTerms({ onError: addError });

  const handleReply = async (reply: string) => {
    if (reply === ConsentReply.Why) {
      addUser("De ce este necesar?");
      addBot(chatCopy.consentWhy);
      setIsWhyAnswered(true);
      return;
    }

    addUser("Da, sunt de acord");
    setIsSaving(true);
    if (
      await acceptTerms({
        includesBc: true,
        acceptsMarketing: false,
        isBeforeKyc: !facts.isKycValid,
      })
    ) {
      onAnswered();
      return;
    }
    setIsSaving(false);
  };

  return (
    <ChatTurnLayout
      messageCount={messages.length + Number(isSaving)}
      thread={
        <>
          <BotMessage lines={preliminaryConsentLines} isTyped={isPromptTyped} />
          <TurnMessages messages={messages} />
          {isSaving && <TypingIndicator />}
        </>
      }
      actions={
        !isSaving && (
          <QuickReplies
            onSelect={(reply) => void handleReply(reply)}
            replies={[
              { id: ConsentReply.Accept, label: "Da, sunt de acord 👍" },
              ...(isWhyAnswered
                ? []
                : [{ id: ConsentReply.Why, label: "De ce este necesar? 🤔" }]),
            ]}
          />
        )
      }
    />
  );
}
